import { Box, IconButton } from "@mui/material";
import React, { FC, useMemo, useState } from "react";
import TableSearchInput from "../../../components/TableSearchInput";
import { GridColDef, GridPaginationModel, GridRenderCellParams } from "@mui/x-data-grid";
import { AGDataGrid } from "../../../components/AGDataGrid";
import moment from "moment";
import { useGetUserActivityLogsQuery } from "../../../redux/reducers/userManagement.reducer";
import { IGetUserActivityLogsRequestData } from "../../../types";
import { ROWS_PER_PAGE } from "../../../utils/constants";
import { InfoOutlined } from "@mui/icons-material";
import UserActivityDetailsDialog from "./ActivityLogDetailsDialog";

export interface IUserActivityDetails {
	id: number;
	timestamp: string;
	full_name: string;
	username: string;
	activity: string;
	type: "low" | "medium" | "high" | null;
	type_text: string;
}

const ActivityLogs: FC = () => {
	const [filters, setFilters] = useState<IGetUserActivityLogsRequestData>({ page: 1 });
	const [selectedActivityDetails, setSelectedActivityDetails] = useState<IUserActivityDetails | null>(null);

	// GET USER ACTIVITY LOGS
	const {
		data: getUserActivityLogsResponse,
		isLoading: getUserActivityLogsLoading,
		isFetching: getUserActivityLogsFetching
	} = useGetUserActivityLogsQuery(filters, { refetchOnMountOrArgChange: true });

	const rowsData = useMemo<IUserActivityDetails[]>(() => {
		if (!getUserActivityLogsResponse) return [];

		return getUserActivityLogsResponse.results.map((item) => ({
			id: item.id,
			timestamp: item.timestamp,
			full_name: item.performed_by_details
				? `${item.performed_by_details.first_name} ${item.performed_by_details.last_name}`.trim()
				: item.performed_on_details
				? `${item.performed_on_details.first_name} ${item.performed_on_details.last_name}`.trim()
				: "--",
			username: item.performed_by_details?.username || item.performed_on_details?.username || "--",
			activity: item.message || "",
			type: item.severity,
			type_text: item.severity_display
		}));
	}, [getUserActivityLogsResponse]);

	function handleSubmitSearchInput(searchInput: string) {
		setFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };

			if (searchInput) updatedFilters.search = searchInput;
			else delete updatedFilters.search;

			return updatedFilters;
		});
	}

	function handlePageChange(event: GridPaginationModel) {
		setFilters((currentFilters) => ({
			...currentFilters,
			page: event.page + 1
		}));
	}

	function openActivityDetailsDialog(activityDetails: IUserActivityDetails) {
		setSelectedActivityDetails(activityDetails);
	}

	function closeActivityDetailsDialog() {
		setSelectedActivityDetails(null);
	}

	const columnsData: GridColDef[] = [
		{
			field: "timestamp",
			headerName: "Timestamp",
			flex: 0.5,
			minWidth: 120,
			sortable: false,
			align: "center",
			headerAlign: "center",
			valueFormatter: (value: string) => moment(value).format("DD/MM/YYYY hh:mm A")
		},
		{
			field: "full_name",
			headerName: "Full Name",
			flex: 1,
			minWidth: 120,
			sortable: false,
			align: "center",
			headerAlign: "center"
		},
		{
			field: "username",
			headerName: "Username",
			flex: 0.5,
			minWidth: 120,
			sortable: false,
			align: "center",
			headerAlign: "center"
		},
		{
			field: "type",
			headerName: "Severity",
			flex: 1,
			maxWidth: 120,
			sortable: false,
			headerAlign: "center",
			renderCell: (params: GridRenderCellParams<IUserActivityDetails, "low" | "medium" | "high" | null>) => {
				const colorsMap: Record<"low" | "medium" | "high", { bgcolor: string; color: string }> = {
					low: { bgcolor: "#e3f2fd", color: "#1565c0" },
					medium: { bgcolor: "#fff3e0", color: "#ef6c00" },
					high: { bgcolor: "#ffebee", color: "#c62828" }
				};

				const colors = colorsMap[params.value || "low"];

				return (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
						<Box
							sx={{
								...colors,
								padding: "6px 12px",
								borderRadius: "16px",
								fontSize: "0.75rem",
								fontWeight: 600,
								textTransform: "capitalize",
								display: "inline-block",
								lineHeight: 1,
								whiteSpace: "nowrap"
							}}
						>
							{params.row.type_text || "Low"}
						</Box>
					</Box>
				);
			}
		},
		{
			field: "activity",
			headerName: "Activity",
			flex: 2,
			minWidth: 250,
			sortable: false
		},
		{
			field: "actions",
			headerName: "",
			width: 50,
			sortable: false,
			renderCell: (params: GridRenderCellParams<IUserActivityDetails>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					<IconButton size="small" color="info" onClick={() => openActivityDetailsDialog(params.row)}>
						<InfoOutlined fontSize="inherit" />
					</IconButton>
				</Box>
			)
		}
	];

	return (
		<Box className="page-content-wrapper">
			<Box className="page-header-wrapper">
				<Box />
				<TableSearchInput showClearButton={!!filters.search} onSubmit={handleSubmitSearchInput} />
			</Box>

			<Box className="page-content">
				<AGDataGrid
					columns={columnsData}
					rows={rowsData}
					paginationMode="server"
					paginationModel={{ page: filters.page - 1, pageSize: ROWS_PER_PAGE }}
					onPaginationModelChange={handlePageChange}
					rowCount={getUserActivityLogsResponse?.count ?? 0}
					loading={getUserActivityLogsLoading || getUserActivityLogsFetching}
					hideFooter={(getUserActivityLogsResponse?.count ?? 0) <= ROWS_PER_PAGE}
				/>
			</Box>

			<UserActivityDetailsDialog
				open={!!selectedActivityDetails}
				activityDetails={selectedActivityDetails}
				onClose={closeActivityDetailsDialog}
			/>
		</Box>
	);
};

export default ActivityLogs;
